/**
 * Transient messages: a leak, a purchase refused, a round cleared.
 *
 * Toasts stack downward from under the top band, never cover the playfield
 * centre, and never steal focus. Each kind owns one icon from the vendored set,
 * so the player learns the mark before they finish reading the words.
 */

import { el, icon, installUiTheme, money, prefersReducedMotion } from './Icon';
import type { IconName } from './Icon';
import type { Role } from '@/core/palette';

export type ToastKind = 'info' | 'success' | 'warn' | 'error' | 'cash' | 'wave';

export interface ToastSpec {
  /** Stable id. Repeating an id refreshes the existing toast instead of stacking. */
  id?: string;
  kind?: ToastKind;
  text: string;
  /** Rendered after the text in the kind's colour. Cash is formatted as money. */
  amount?: number;
  /** Milliseconds on screen. */
  ttl?: number;
  /** Overrides the kind's icon when a message has a better mark of its own. */
  icon?: IconName;
}

export interface ToastState {
  /** Anything not already on screen is pushed, matched by id. */
  toasts?: readonly ToastSpec[];
}

const KIND_ICON: Record<ToastKind, IconName> = {
  info: 'info-circle',
  success: 'check-circle',
  warn: 'exclamation-triangle',
  error: 'octagon-times',
  cash: 'coins',
  wave: 'flag-checkered',
};

const KIND_ROLE: Record<ToastKind, Role> = {
  info: 'uiAccent',
  success: 'uiWave',
  warn: 'uiCash',
  error: 'uiDanger',
  cash: 'uiCash',
  wave: 'uiWave',
};

const KIND_VAR: Record<ToastKind, string> = {
  info: 'var(--ui-accent)',
  success: 'var(--ui-wave)',
  warn: 'var(--ui-cash)',
  error: 'var(--ui-danger)',
  cash: 'var(--ui-cash)',
  wave: 'var(--ui-wave)',
};

const DEFAULT_TTL = 2600;
const MAX_VISIBLE = 4;

interface Live {
  id: string;
  node: HTMLElement;
  timer: number;
}

export class Toast {
  private root = el('div', 'toasts');
  private live: Live[] = [];
  private seq = 0;
  private mounted = false;

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    installUiTheme();
    this.root.setAttribute('role', 'status');
    this.root.setAttribute('aria-live', 'polite');
    root.append(this.root);
  }

  /** The direct API. `update` exists so the panel honours the common contract. */
  push(spec: ToastSpec): void {
    if (!this.mounted) return;
    const id = spec.id ?? `t${++this.seq}`;
    const existing = this.live.find((l) => l.id === id);
    if (existing) {
      this.dismissNow(existing);
    }

    const kind = spec.kind ?? 'info';
    const node = el('div', 'toast');
    node.style.setProperty('--toast-col', KIND_VAR[kind]);
    node.append(
      icon(spec.icon ?? KIND_ICON[kind], { size: 16, role: KIND_ROLE[kind], glow: 0.9 }),
      el('span', 'toast__text', spec.text),
    );
    if (spec.amount !== undefined) {
      const value = kind === 'cash'
        ? `${spec.amount >= 0 ? '+' : '-'}${money(Math.abs(spec.amount))}`
        : String(spec.amount);
      node.append(el('span', 'toast__amount num', value));
    }

    this.root.append(node);
    const ttl = spec.ttl ?? DEFAULT_TTL;
    const entry: Live = {
      id,
      node,
      timer: window.setTimeout(() => this.dismiss(entry), ttl),
    };
    this.live.push(entry);

    while (this.live.length > MAX_VISIBLE) {
      const oldest = this.live.shift();
      if (oldest) this.dismissNow(oldest);
    }
  }

  update(state: ToastState): void {
    if (!state.toasts) return;
    for (const spec of state.toasts) {
      if (spec.id && this.live.some((l) => l.id === spec.id)) continue;
      this.push(spec);
    }
  }

  clear(): void {
    // Drain, don't iterate: dismissNow splices the array it would be walking,
    // which skipped every other toast — zombies from the previous run stayed
    // on screen until their own ttl got around to them.
    while (this.live.length > 0) this.dismissNow(this.live[0]);
  }

  destroy(): void {
    this.clear();
    this.root.remove();
    this.mounted = false;
  }

  private dismiss(entry: Live): void {
    const i = this.live.indexOf(entry);
    if (i >= 0) this.live.splice(i, 1);
    if (prefersReducedMotion()) {
      entry.node.remove();
      return;
    }
    entry.node.classList.add('is-leaving');
    window.setTimeout(() => entry.node.remove(), 180);
  }

  private dismissNow(entry: Live): void {
    window.clearTimeout(entry.timer);
    const i = this.live.indexOf(entry);
    if (i >= 0) this.live.splice(i, 1);
    entry.node.remove();
  }
}
